import { useEffect } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { UbpContextProvider } from '@/components/usage-based-pricing-calculator/src/context/ubp-context';
import { useLoadProductCatalogData } from '@/components/usage-based-pricing-calculator/src/hooks/hooks';
import { UsageBasedPricingCalculator } from '@/components/usage-based-pricing-calculator-q4/src/usage-based-pricing-calculator';
import { PricingPageLayout } from '@/components/wpl/pricing/src/pricing-page-layout/pricing-page-layout';
import { wacToken } from '@/libraries/design/foundations';
import { GlobalNav } from '@/patterns/global-nav/src/nav-global';

export const UsageBasedPricingPage = () => {
	return (
		<PricingPageLayout nav={<GlobalNav productKey="usage-based-pricing" />} noInlinePadding>
			<UbpContextProvider>
				<UsageBasedPricingShell />
			</UbpContextProvider>
		</PricingPageLayout>
	);
};

/**
 * Inner shell — sits inside PricingPageLayout's <main>. Renders the header,
 * the calculator state-machine driven step view, and the legacy wizard
 * (industry/team-size/growth/feature/solution + capacity sidebar) so behaviour
 * is preserved while the new Q4 calculator is rolled out.
 */
const UsageBasedPricingShell = () => {
	const loadData = useLoadProductCatalogData();
	useEffect(() => {
		void loadData();
	}, [loadData]);

	return (
		<div css={shellBackgroundStyles}>
			<div css={shellInnerStyles}>
				<section
					css={stepLayoutStyles}
					aria-label="Usage-based pricing calculator"
					data-testid="ubp-calculator-shell"
				>
					<UsageBasedPricingCalculator />
				</section>
			</div>
		</div>
	);
};

/**
 * Outer container. Provides a flat white background for the calculator
 * viewport.
 */
const shellBackgroundStyles = css({
	position: 'relative',
	width: '100%',
	overflow: 'hidden',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '20px',
	// Top padding removed — the calculator content controls its own top
	// spacing (via the stepper margin / H1 padding). Bottom padding stays so
	// the calculator doesn't butt against the shell edge.
	paddingBlockStart: 0,
	paddingBlockEnd: token('space.500'),
	[media.below.md]: {
		paddingBlockStart: 0,
		paddingBlockEnd: token('space.400'),
		borderRadius: '16px',
	},
	[media.below.sm]: {
		paddingBlockStart: 0,
		paddingBlockEnd: token('space.300'),
		borderRadius: '12px',
	},
});

const shellInnerStyles = css({
	position: 'relative',
	zIndex: 1,
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.500'),
	width: '100%',
	maxWidth: '1152px',
	marginInline: 'auto',
	// Responsive inline padding sourced from Figma per breakpoint:
	//   Desktop XL (default)   : 0   — flush to shell edges at ≥1440px where
	//                                  the shell has room to breathe
	//   Desktop→Tablet (<1440) : 60  — kicks in as soon as the viewport drops
	//                                  below the shell's 1152px maxWidth plus
	//                                  breathing room, so the calculator never
	//                                  clips to the viewport edges. Matches
	//                                  Figma tablet node 1160:27402 (px-[60px]).
	//   Mobile (<768px)        : 32  — Figma mobile node 1160:27591 (children
	//                                  at x=32 in a 375-wide frame).
	// Applied at the container level so every step inherits the same gutters.
	paddingInline: 0,
	[media.below.lg]: {
		paddingInline: '60px',
	},
	[media.below.md]: {
		gap: token('space.400'),
		paddingInline: '60px',
	},
	[media.below.sm]: {
		gap: token('space.300'),
		paddingInline: '32px',
	},
});

/**
 * Step layout wrapper — every calculator step renders inside this so the
 * spacing rhythm, max-width, and inline padding are consistent across the
 * usage / needs / estimate / allowance / summary steps.
 */
const stepLayoutStyles = css({
	display: 'flex',
	flexDirection: 'column',
	// Center the calculator horizontally via flex alignment instead of
	// margin-auto on the inner element — this avoids adding left/right
	// margins to the calculator itself and keeps its box model clean.
	alignItems: 'center',
	gap: token('space.400'),
	width: '100%',
});
